import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, BarChart3, TrendingUp, MessageSquare, Target, Users, Eye, Lightbulb, AlertCircle, CheckCircle, Loader2, Download, FileText } from 'lucide-react';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';

const ContentAnalytics = () => {
  const { updateCreditsFromResponse } = useCredits();
  const [files, setFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    setError('');
    setAnalysis(null);
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!files.length) {
      setError('Please upload at least one file.');
      return;
    }
    
    setIsAnalyzing(true);
    setError('');
    setAnalysis(null);
    
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      
      const response = await axios.post(getApiUrl('/analyze_content'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      
      setAnalysis(response.data.analysis);
      updateCreditsFromResponse(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to analyze content. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 8) return 'bg-green-100';
    if (score >= 6) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const ScoreCard = ({ title, score, description, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Icon className="w-6 h-6 text-blue-600 mr-3" />
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getScoreBgColor(score)} ${getScoreColor(score)}`}>
          {score}/10
        </div>
      </div>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );

  const SuggestionCard = ({ suggestion }) => {
    const getTypeColor = (type) => {
      switch (type) {
        case 'wording': return 'border-blue-500 bg-blue-50';
        case 'structure': return 'border-purple-500 bg-purple-50';
        case 'messaging': return 'border-green-500 bg-green-50';
        case 'call_to_action': return 'border-orange-500 bg-orange-50';
        default: return 'border-gray-500 bg-gray-50';
      }
    };

    const getTypeIcon = (type) => {
      switch (type) {
        case 'wording': return '✏️';
        case 'structure': return '🏗️';
        case 'messaging': return '💬';
        case 'call_to_action': return '🎯';
        default: return '💡';
      }
    };

    return (
      <div className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${getTypeColor(suggestion.type)}`}>
        <div className="flex items-start mb-3">
          <div className="text-2xl mr-3 mt-0.5">{getTypeIcon(suggestion.type)}</div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 capitalize mb-2">
              {suggestion.type.replace('_', ' ')} Improvement
            </h4>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Original:</p>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border-l-2 border-gray-300">{suggestion.original}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Suggestion:</p>
                <p className="text-sm text-green-700 bg-green-50 p-3 rounded border-l-2 border-green-300">{suggestion.suggestion}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Why this helps:</p>
                <p className="text-sm text-gray-600 bg-blue-50 p-2 rounded">{suggestion.reason}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Content Analytics & Insights
          </h1>
          <p className="text-xl text-gray-600">
            Analyze your content for themes, sentiment, and messaging effectiveness
          </p>
        </div>

        {!analysis ? (
          <div className="max-w-4xl mx-auto">
            {/* File Upload Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Upload Content to Analyze</h2>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="content-upload"
                  accept=".pdf,.doc,.docx,.txt,.md,.pptx"
                />
                <label htmlFor="content-upload" className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    Choose files
                  </span>
                  <span className="text-gray-500"> or drag and drop</span>
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  PDF, DOC, DOCX, TXT, MD, PPTX files accepted
                </p>
                {files.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700">Selected files:</p>
                    <ul className="mt-2 space-y-1">
                      {files.map((file, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-center">
                          <FileText className="w-4 h-4 mr-2" />
                          {file.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-red-800">{error}</span>
                </div>
              </div>
            )}

            {/* Analyze Button */}
            <div className="flex justify-center">
              <button
                onClick={handleAnalyze}
                disabled={isAnalyzing || files.length === 0}
                className={`px-8 py-3 text-lg font-medium rounded-md text-white ${
                  isAnalyzing || files.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {isAnalyzing ? (
                  <div className="flex items-center">
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                    Analyzing Content...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Analyze Content
                  </div>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Navigation Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'overview', name: 'Overview', icon: BarChart3 },
                  { id: 'themes', name: 'Themes & Sentiment', icon: TrendingUp },
                  { id: 'messaging', name: 'Messaging Analysis', icon: MessageSquare },
                  { id: 'suggestions', name: 'Improvement Suggestions', icon: Lightbulb }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 mr-2" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <ScoreCard
                    title="Overall Score"
                    score={analysis?.overall_score || 0}
                    description="Overall content effectiveness score"
                    icon={BarChart3}
                  />
                  <ScoreCard
                    title="Messaging Effectiveness"
                    score={analysis?.messaging_effectiveness?.score || 0}
                    description="How well the content communicates value"
                    icon={MessageSquare}
                  />
                  <ScoreCard
                    title="Sales Focus"
                    score={analysis?.sales_focus?.score || 0}
                    description="How sales-oriented the content is"
                    icon={Target}
                  />
                  <ScoreCard
                    title="Customer-Centricity"
                    score={analysis?.customer_centricity?.score || 0}
                    description="How customer-focused the messaging is"
                    icon={Users}
                  />
                  <ScoreCard
                    title="Clarity & Readability"
                    score={analysis?.clarity_readability?.score || 0}
                    description="Content clarity and accessibility"
                    icon={Eye}
                  />
                  <ScoreCard
                    title="Actionability"
                    score={analysis?.actionability?.score || 0}
                    description="How actionable the content is"
                    icon={TrendingUp}
                  />
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Summary</h3>
                  <p className="text-gray-700">{analysis?.summary || 'No summary available'}</p>
                </div>
              </div>
            )}

            {/* Themes & Sentiment Tab */}
            {activeTab === 'themes' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Key Themes</h3>
                    <div className="space-y-2">
                      {analysis?.themes?.map((theme, index) => (
                        <div key={index} className="flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{theme}</span>
                        </div>
                      )) || <p className="text-gray-500">No themes identified</p>}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Sentiment Analysis</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Overall Sentiment:</span>
                        <span className={`font-semibold capitalize ${
                          analysis?.sentiment?.overall === 'positive' ? 'text-green-600' :
                          analysis?.sentiment?.overall === 'negative' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {analysis?.sentiment?.overall || 'neutral'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Confidence:</span>
                        <span className="font-semibold text-gray-800">
                          {analysis?.sentiment?.confidence ? (analysis.sentiment.confidence * 100).toFixed(0) : '0'}%
                        </span>
                      </div>
                      <div className="mt-4">
                        <p className="text-sm text-gray-600">{analysis?.sentiment?.details || 'No sentiment details available'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Messaging Analysis Tab */}
            {activeTab === 'messaging' && (
              <div className="space-y-6">
                {['messaging_effectiveness', 'sales_focus', 'customer_centricity', 'clarity_readability', 'actionability'].map((category) => (
                  <div key={category} className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 capitalize">
                      {category.replace('_', ' ')}
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Assessment</h4>
                        <p className="text-gray-600 text-sm">{analysis?.[category]?.assessment || 'No assessment available'}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Improvements</h4>
                        <ul className="space-y-1">
                          {analysis?.[category]?.improvements?.map((improvement, index) => (
                            <li key={index} className="text-sm text-gray-600 flex items-start">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-2"></div>
                              {improvement}
                            </li>
                          )) || <li className="text-sm text-gray-500">No improvements suggested</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Improvement Suggestions Tab */}
            {activeTab === 'suggestions' && (
              <div className="space-y-6">
                {/* Summary Section */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">💡 Improvement Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(() => {
                      const typeCounts = analysis?.specific_suggestions?.reduce((acc, suggestion) => {
                        acc[suggestion.type] = (acc[suggestion.type] || 0) + 1;
                        return acc;
                      }, {}) || {};
                      
                      return Object.entries(typeCounts).map(([type, count]) => (
                        <div key={type} className="text-center">
                          <div className="text-2xl mb-1">
                            {type === 'wording' ? '✏️' : 
                             type === 'structure' ? '🏗️' : 
                             type === 'messaging' ? '💬' : 
                             type === 'call_to_action' ? '🎯' : '💡'}
                          </div>
                          <div className="text-sm font-medium text-gray-700 capitalize">{type.replace('_', ' ')}</div>
                          <div className="text-lg font-bold text-blue-600">{count}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Grouped Suggestions */}
                <div className="space-y-8">
                  {['wording', 'structure', 'messaging', 'call_to_action'].map(type => {
                    const typeSuggestions = analysis?.specific_suggestions?.filter(s => s.type === type) || [];
                    if (typeSuggestions.length === 0) return null;
                    
                    return (
                      <div key={type} className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl">
                            {type === 'wording' ? '✏️' : 
                             type === 'structure' ? '🏗️' : 
                             type === 'messaging' ? '💬' : 
                             type === 'call_to_action' ? '🎯' : '💡'}
                          </div>
                          <h3 className="text-xl font-semibold text-gray-800 capitalize">
                            {type.replace('_', ' ')} Improvements ({typeSuggestions.length})
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          {typeSuggestions.map((suggestion, index) => (
                            <SuggestionCard key={index} suggestion={suggestion} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {(!analysis?.specific_suggestions || analysis.specific_suggestions.length === 0) && (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">🎉</div>
                    <p className="text-gray-500 text-lg">Great job! No specific improvements needed.</p>
                  </div>
                )}
              </div>
            )}



            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setAnalysis(null);
                  setFiles([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Analyze New Content
              </button>
              <button
                onClick={async () => {
                  setIsDownloading(true);
                  try {
                    const response = await fetch(getApiUrl('/download_analysis_pdf'), {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      credentials: 'include',
                      body: JSON.stringify({ analysis })
                    });
                    
                    if (response.ok) {
                      // Create blob from response
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `content_analysis_report_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } else {
                      const errorData = await response.json();
                      alert(`Error downloading PDF: ${errorData.error || 'Unknown error'}`);
                    }
                  } catch (error) {
                    console.error('Error downloading PDF:', error);
                    alert('Error downloading PDF. Please try again.');
                  } finally {
                    setIsDownloading(false);
                  }
                }}
                disabled={isDownloading}
                className={`px-4 py-2 text-white rounded-lg flex items-center ${
                  isDownloading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentAnalytics; 